import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserName} from '../../utils/authUtils.ts';
import { register_call, stop_call } from '../../services/api.js';
import { QurveSDK } from '../../utils/qurve-sdk';

const VoiceChat = () => {
  const [isCallActive, setIsCallActive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callStatus, setCallStatus] = useState('Ready to Connect');
  const [isInitializing, setIsInitializing] = useState(false);
  const mountedRef = useRef(true);
  const qurveSdkRef = useRef(null);
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userName = getUserName();
    if (!token || !userName) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      qurveSdkRef.current?.abortCall().catch(error => console.error('Error ending call:', error));
      qurveSdkRef.current = null;
    };
  }, []);

  const handleCallToggle = async () => {
    if (isInitializing) return;
    if (qurveSdkRef.current) {
      await qurveSdkRef.current.abortCall();
      return;
    }

    setIsInitializing(true);
    setCallStatus('Connecting...');
    let failed = false;
    const sdk = new QurveSDK({
      registerCall: register_call,
      stopCall: stop_call,
      onCallStart: () => {
        if (!mountedRef.current || qurveSdkRef.current !== sdk) return;
        setIsCallActive(true);
        setCallStatus('Connected');
      },
      onCallEnd: () => {
        if (!mountedRef.current || qurveSdkRef.current !== sdk) return;
        qurveSdkRef.current = null;
        setIsCallActive(false);
        setIsMuted(false);
        if (!failed) setCallStatus('Ready to Connect');
      },
      onError: error => {
        if (!mountedRef.current || qurveSdkRef.current !== sdk) return;
        failed = true;
        console.error('Voice call failed:', error);
        setCallStatus('Connection Failed');
      },
    });
    qurveSdkRef.current = sdk;
    try {
      await sdk.startCall();
    } catch (error) {
      if (mountedRef.current) {
        setIsCallActive(false);
        setCallStatus('Connection Failed');
      }
    } finally {
      if (mountedRef.current) setIsInitializing(false);
    }
  };

  const handleMuteToggle = () => {
    if (!qurveSdkRef.current || !isCallActive) return;
    if (isMuted) {
      qurveSdkRef.current.unmuteCall();
      setCallStatus('Connected');
    } else {
      qurveSdkRef.current.muteCall();
      setCallStatus('Muted');
    }
    setIsMuted(!isMuted);
  };


  return (
    <div className="flex flex-col h-full bg-gray-900">

      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-2xl w-full mx-auto p-8">
          <div className="bg-gray-800 rounded-2xl p-12 shadow-2xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Voice Chat Testing</h2>
              <p className="text-gray-400 text-lg">Test your voice interaction with Qurve AI</p>
            </div>

            <div className="relative flex justify-center mb-12">
              <div className="relative">
                <div className={`w-64 h-64 rounded-full ${isCallActive ? 'bg-indigo-900/30' : 'bg-gray-700/30'} flex items-center justify-center`}>
                  <div className={`w-48 h-48 rounded-full ${isCallActive ? 'bg-indigo-800/40' : 'bg-gray-600/40'} flex items-center justify-center`}>
                    <div className={`w-32 h-32 rounded-full ${isCallActive ? 'bg-indigo-700/50 pulse-animation' : 'bg-gray-500/50'} flex items-center justify-center`}>
                      {isCallActive ? (
                        <div className="flex gap-1">
                          {[...Array(5)].map((_, index) => (
                            <div
                              key={index}
                              className="w-2 bg-white rounded-full animate-bounce"
                              style={{
                                height: '30px',
                                animationDelay: `${index * 150}ms`,
                                animationDuration: '1s',
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        <i className="fas fa-microphone text-5xl text-gray-300"></i>
                      )}
                    </div>
                  </div>
                </div>
                {isCallActive && (
                  <div className="absolute inset-0">
                    <div className="w-full h-full rounded-full animate-ping bg-indigo-500/20"></div>
                  </div>
                )}
              </div>
            </div>

            <div className="text-center mb-12">
              <div className="inline-flex items-center px-6 py-3 rounded-full bg-gray-700/50">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  isCallActive ? 'bg-green-500' : 
                  isInitializing ? 'bg-yellow-500' : 
                  'bg-gray-500'
                }`}></div>
                <span className="text-lg font-medium text-white">
                  {isInitializing ? 'Initializing...' : callStatus}
                </span>
              </div>
            </div>

            <div className="flex justify-center items-center gap-6">
              <button 
                aria-label={isCallActive ? 'End voice call' : 'Start voice call'}
                onClick={handleCallToggle}
                disabled={isInitializing}
                className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl transition-all duration-300 shadow-lg ${
                  isInitializing 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : isCallActive 
                      ? 'bg-red-600 hover:bg-red-700' 
                      : 'bg-green-600 hover:bg-green-700'
                } !rounded-button`}
              >
                {isInitializing ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  <i className={`fas ${isCallActive ? 'fa-phone-slash transform rotate-90' : 'fa-phone'}`}></i>
                )}
              </button>
              {isCallActive && (
                <button 
                  aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
                  onClick={handleMuteToggle}
                  className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-xl transition-all duration-300 shadow-lg ${
                    isMuted 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-gray-600 hover:bg-gray-700'
                  } !rounded-button`}
                >
                  <i className={`fas ${isMuted ? 'fa-microphone' : 'fa-microphone-slash'}`}></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .pulse-animation {
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          }
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}
      </style>
    </div>
  );
};

export default VoiceChat;
